import type { LoadableComponent } from '@loadable/component';
import loadable from '@loadable/component';
import React from 'react';

import ConsentOverlay from '@components/ConsentOverlay';
import PrivateRoute from '@components/PrivateRoute';
import Seo from '@components/SEO';

import { DrawerProvider, AppLayout as IAppLayout } from '@contexts/index';

import { useAppLayout } from '@hooks/useAppLayout';
import { useIsMobile } from '@hooks/useIsMobile';
import { useSession } from '@hooks/useSession';

const AdminLayout = loadable(() => import('./AdminLayout'));
const CuratorLayout = loadable(() => import('./CuratorLayout'));
const NonPatientLayout = loadable(() => import('./NonPatientLayout'));
const PractitionerLayout = loadable(() => import('./PractitionerLayout'));
const UserLayout = loadable(() => import('./UserLayout'));

interface AppLayoutProps {
  friendlyName?: string | null;
  children: React.ReactNode;
  loading?: boolean;
  showHeader?: boolean;
  /**
   * Limits the view height. Only applies to non-admin users on mobile.
   */
  limitHeight?: boolean;
}

function AppLayout({
  children = null,
  loading = false,
  limitHeight = false,
  showHeader = false,
  ...props
}: AppLayoutProps) {
  const { state } = useAppLayout();
  const mobile = useIsMobile();
  const {
    state: { isAdmin, isClient, isCurator, isPractitioner, isPatient, isUser },
  } = useSession();

  const isCaregiver = isClient && isUser && !isPatient;

  const friendlyName = props.friendlyName ?? state.title;

  const components = (
    <>
      <Seo title={friendlyName} />
      <ConsentOverlay />
    </>
  );

  let Layout: LoadableComponent<AppLayoutProps & { mobile?: boolean }>;
  if (isAdmin) {
    Layout = AdminLayout;
  } else if (isCurator) {
    // Layout for curator and curator admin users
    Layout = CuratorLayout;
  } else if (isPractitioner) {
    // Layout for practitioner and org admin users
    Layout = PractitionerLayout;
  } else if (isCaregiver) {
    Layout = NonPatientLayout;
  } else {
    Layout = UserLayout;
  }

  return (
    <PrivateRoute>
      {components}
      <Layout
        friendlyName={friendlyName}
        loading={loading}
        mobile={mobile}
        showHeader={showHeader}
        limitHeight={limitHeight}
      >
        {children}
      </Layout>
    </PrivateRoute>
  );
}

const AppLayoutWithProvider: React.FC<AppLayoutProps> = ({
  children,
  ...props
}) => {
  return (
    <IAppLayout.Provider>
      <DrawerProvider>
        <AppLayout {...props}>{children}</AppLayout>
      </DrawerProvider>
    </IAppLayout.Provider>
  );
};

export default AppLayoutWithProvider;
